import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';

export type MinigameDescriptor = {
  id: string;
  name: string;
  description: string;
  iconUrl?: string;
};

export type MinigameAnnouncePayload = BoardMountPayload & {
  minigameList: MinigameDescriptor[];
  selectedIndex: number;
  onComplete?: () => void;
};

const Z_INDEX_OVERLAY = 350;
const OVERLAY_BG = 'rgba(0, 0, 0, 0.68)';
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";
const BODY_FAMILY = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif";
const TITLE_TEXT_SHADOW = '0 3px 0 #000000, 0 8px 18px rgba(0,0,0,0.52)';
const CARD_WIDTH = 132;
const CARD_HEIGHT = 148;
const CARD_GAP = 16;
const REEL_WIDTH_FALLBACK = 760;
const SPIN_SECONDS = 2.8;
const REVEAL_SECONDS = 0.8;

function normalizeDescriptor(descriptor: MinigameDescriptor | undefined): MinigameDescriptor {
  return {
    id: descriptor?.id ?? 'unknown',
    name: descriptor?.name?.trim() || 'Mystery Minigame',
    description: descriptor?.description?.trim() || 'Get ready.',
    ...(descriptor?.iconUrl === undefined ? {} : { iconUrl: descriptor.iconUrl }),
  };
}

function buildIconUrl(descriptor: MinigameDescriptor): string | undefined {
  if (descriptor.iconUrl !== undefined) return descriptor.iconUrl;
  if (descriptor.id === 'unknown' || descriptor.id.startsWith('_')) return undefined;
  return `/assets/textures/minigame-icons/${descriptor.id}.webp`;
}

function buildCard(descriptor: MinigameDescriptor): HTMLElement {
  const card = document.createElement('div');
  card.className = 'minigame-announce__card';
  card.dataset.minigameId = descriptor.id;
  card.style.cssText = [
    `width: ${CARD_WIDTH}px`,
    `height: ${CARD_HEIGHT}px`,
    'box-sizing: border-box',
    'flex: 0 0 auto',
    'border-radius: 8px',
    'border: 2px solid rgba(255,255,255,0.18)',
    'box-shadow: 0 12px 28px rgba(0,0,0,0.26)',
    'background: rgba(31,39,52,0.9)',
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'justify-content: center',
    'padding: 12px',
    'user-select: none',
  ].join('; ');

  const iconWrap = document.createElement('div');
  iconWrap.className = 'minigame-announce__icon';
  iconWrap.style.cssText = [
    'width: 72px',
    'height: 72px',
    'border-radius: 8px',
    'background: rgba(255,255,255,0.08)',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'overflow: hidden',
    'margin-bottom: 10px',
  ].join('; ');
  const iconUrl = buildIconUrl(descriptor);
  if (iconUrl != null) {
    const img = document.createElement('img');
    img.src = iconUrl;
    img.alt = '';
    img.draggable = false;
    img.style.cssText = ['width: 100%', 'height: 100%', 'object-fit: contain'].join('; ');
    img.addEventListener('error', () => {
      img.remove();
      iconWrap.textContent = descriptor.name.charAt(0).toUpperCase();
    });
    iconWrap.appendChild(img);
  } else {
    iconWrap.textContent = descriptor.name.charAt(0).toUpperCase();
    iconWrap.style.font = `34px ${HEADLINE_FAMILY}`;
    iconWrap.style.color = '#ffffff';
  }
  card.appendChild(iconWrap);

  const label = document.createElement('div');
  label.className = 'minigame-announce__label';
  label.textContent = descriptor.name;
  label.style.cssText = [
    `font-family: ${BODY_FAMILY}`,
    'font-weight: 900',
    'font-size: 14px',
    'line-height: 1.12',
    'color: #ffffff',
    'text-align: center',
    'overflow-wrap: anywhere',
    'max-width: 100%',
  ].join('; ');
  card.appendChild(label);

  return card;
}

export function createMinigameAnnounceScreen(
  ctx: ScreenMountContext<MinigameAnnouncePayload>,
): ScreenHandle {
  const { gsapRegistry, host, payload } = ctx;
  const baseList = payload.minigameList.length > 0 ? payload.minigameList : [undefined];
  const selectedIndex =
    payload.selectedIndex >= 0 && payload.selectedIndex < baseList.length
      ? payload.selectedIndex
      : 0;
  const minigameList = baseList.map((entry) => normalizeDescriptor(entry));
  const selected = minigameList[selectedIndex] ?? normalizeDescriptor(undefined);

  let disposed = false;
  const spinHandle = gsapRegistry.create('minigame-announce:selector');

  const root = document.createElement('div');
  root.className = 'screen-minigame-announce';
  root.setAttribute('data-screen-id', 'minigame-announce');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_OVERLAY}`,
    `background: ${OVERLAY_BG}`,
    'display: grid',
    'grid-template-rows: minmax(80px, 0.9fr) auto minmax(120px, 1fr)',
    'align-items: center',
    'justify-items: center',
    'overflow: hidden',
    'padding: 28px 0',
    'box-sizing: border-box',
  ].join('; ');

  const title = document.createElement('div');
  title.textContent = 'MINIGAME';
  title.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    'font-size: clamp(30px, 6vw, 58px)',
    'letter-spacing: 0',
    'color: #ffffff',
    `text-shadow: ${TITLE_TEXT_SHADOW}`,
    'align-self: end',
  ].join('; ');
  root.appendChild(title);

  const reel = document.createElement('div');
  reel.className = 'minigame-announce__reel';
  reel.style.cssText = [
    'position: relative',
    'width: min(100vw, 920px)',
    'height: 190px',
    'overflow: hidden',
    'display: flex',
    'align-items: center',
  ].join('; ');
  root.appendChild(reel);

  const track = document.createElement('div');
  track.className = 'minigame-announce__track';
  track.style.cssText = [
    'display: flex',
    `gap: ${CARD_GAP}px`,
    'align-items: center',
    'will-change: transform',
  ].join('; ');
  reel.appendChild(track);

  const repeated: MinigameDescriptor[] = [];
  const loops = Math.max(5, Math.ceil(18 / minigameList.length));
  for (let loop = 0; loop < loops; loop += 1) {
    repeated.push(...minigameList);
  }
  const targetCycle = Math.max(2, loops - 2);
  const targetRepeatedIndex = targetCycle * minigameList.length + selectedIndex;

  repeated.forEach((descriptor) => {
    track.appendChild(buildCard(descriptor));
  });

  const frame = document.createElement('div');
  frame.className = 'minigame-announce__selection-frame';
  frame.style.cssText = [
    'position: absolute',
    'left: 50%',
    'top: 50%',
    `width: ${CARD_WIDTH + 20}px`,
    `height: ${CARD_HEIGHT + 20}px`,
    'transform: translate(-50%, -50%)',
    'border: 3px solid rgba(255,255,255,0.9)',
    'border-radius: 10px',
    'box-shadow: inset 0 0 18px rgba(255,255,255,0.28), 0 0 28px rgba(0,0,0,0.45)',
    'pointer-events: none',
  ].join('; ');
  reel.appendChild(frame);

  for (const side of ['left', 'right'] as const) {
    const fade = document.createElement('div');
    fade.className = `minigame-announce__fade minigame-announce__fade--${side}`;
    fade.style.cssText = [
      'position: absolute',
      'top: 0',
      'bottom: 0',
      `${side}: 0`,
      'width: min(22vw, 190px)',
      `background: linear-gradient(${side === 'left' ? '90deg' : '270deg'}, rgba(0,0,0,0.64), rgba(0,0,0,0))`,
      'pointer-events: none',
    ].join('; ');
    reel.appendChild(fade);
  }

  const reveal = document.createElement('div');
  reveal.className = 'minigame-announce__reveal';
  reveal.style.cssText = [
    'opacity: 0',
    'text-align: center',
    'align-self: start',
    'padding: 0 22px',
    'max-width: 760px',
  ].join('; ');
  root.appendChild(reveal);

  const selectedName = document.createElement('h2');
  selectedName.textContent = selected.name;
  selectedName.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    'font-size: clamp(28px, 5vw, 48px)',
    'letter-spacing: 0',
    'margin: 4px 0 8px',
    'color: #ffffff',
    `text-shadow: ${TITLE_TEXT_SHADOW}`,
  ].join('; ');
  reveal.appendChild(selectedName);

  const selectedDescription = document.createElement('p');
  selectedDescription.textContent = selected.description;
  selectedDescription.style.cssText = [
    `font-family: ${BODY_FAMILY}`,
    'font-size: 17px',
    'line-height: 1.35',
    'margin: 0',
    'color: #d7deea',
  ].join('; ');
  reveal.appendChild(selectedDescription);

  host.appendChild(root);

  const reelWidth = reel.getBoundingClientRect().width || REEL_WIDTH_FALLBACK;
  const targetX = targetRepeatedIndex * (CARD_WIDTH + CARD_GAP) + CARD_WIDTH / 2 - reelWidth / 2;
  const tl = spinHandle.timeline;
  tl.fromTo(
    track,
    { x: 0 },
    {
      x: -targetX,
      duration: SPIN_SECONDS,
      ease: 'power4.out',
    },
    0,
  )
    .fromTo(
      track.children[targetRepeatedIndex] as HTMLElement,
      { scale: 1 },
      { scale: 1.12, duration: 0.28, ease: 'back.out(2.4)' },
      SPIN_SECONDS - 0.08,
    )
    .fromTo(
      reveal,
      { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: 0.32, ease: 'power2.out' },
      SPIN_SECONDS,
    )
    .call(
      () => {
        if (disposed) return;
        payload.onComplete?.();
      },
      undefined,
      SPIN_SECONDS + REVEAL_SECONDS,
    );

  return {
    element: root,
    dispose() {
      if (disposed) return;
      disposed = true;
      spinHandle.dispose();
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}
